'use client';

import * as React from 'react';
import type {
  ApiErrorResponse,
  ProjectsListPanelProps,
  SortKey,
} from '@/types';

import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

import { Plus, SlidersHorizontal, ArrowUpDown, X } from 'lucide-react';
import { ShowProjects } from './show-projects';
import { getMentorName, normalize } from '@/lib';
import WithLoadingErrorWrapper from '../with-loading-error-wrapper';


import { useSearchParams } from 'react-router-dom';

export const ProjectsListPanel: React.FC<ProjectsListPanelProps> = ({
  title = 'Projects',
  projects,
  isLoading,
  isFetching,
  error,
  onRetry,
  createLabel = 'Create Project',
  onCreate,
  className,
  projectType,
  assignedInternalProjects,
  assignedExternalProjects,
  underlinedTabs,
}) => {
  const [searchParams] = useSearchParams();
  const initialSearch = searchParams.get('search') || '';
  const data = projects ?? [];

  // local UI state (standalone)
  const [search, setSearch] = React.useState(initialSearch);
  const [mentorSearch, setMentorSearch] = React.useState('');
  const [statusSet, setStatusSet] = React.useState<Set<string>>(new Set());
  const [sortKey, setSortKey] = React.useState<SortKey>('newest');

  const mentorEnabled = React.useMemo(
    () => data.some((p: any) => normalize(getMentorName(p)) !== ''),
    [data]
  );

  const allStatuses = React.useMemo(() => {
    const set = new Set<string>();
    data.forEach((p: any) => {
      const st = String(p?.project_status ?? '').trim();
      if (st) set.add(st);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [data]);

  const filtered = React.useMemo(() => {
    const q = normalize(search);
    const mq = normalize(mentorSearch);

    let out = data.filter((p: any) => {
      const name = normalize(p?.name);
      const status = String(p?.project_status ?? '').trim();
      const mentor = normalize(getMentorName(p));
      const schoolName = normalize(p?.assigned_schools?.name);
      const createdBy = normalize(p?.created_by);
      const desc = normalize(p?.description);

      const matchesText =
        !q ||
        name.includes(q) ||
        schoolName.includes(q) ||
        createdBy.includes(q) ||
        mentor.includes(q) ||
        desc.includes(q);

      const matchesMentor = !mq || mentor.includes(mq);
      const matchesStatus = statusSet.size === 0 || statusSet.has(status);

      return matchesText && matchesMentor && matchesStatus;
    });

    out = out.slice().sort((a: any, b: any) => {
      const aName = String(a?.name ?? '');
      const bName = String(b?.name ?? '');
      const aDur = Number(a?.project_duration ?? 0);
      const bDur = Number(b?.project_duration ?? 0);
      const aDate = new Date(a?.created_on ?? 0).getTime();
      const bDate = new Date(b?.created_on ?? 0).getTime();

      switch (sortKey) {
        case 'oldest':
          return aDate - bDate;
        case 'name_az':
          return aName.localeCompare(bName);
        case 'name_za':
          return bName.localeCompare(aName);
        case 'duration_high':
          return bDur - aDur;
        case 'duration_low':
          return aDur - bDur;
        case 'newest':
        default:
          return bDate - aDate;
      }
    });

    return out;
  }, [data, search, mentorSearch, statusSet, sortKey]);

  const hasActive =
    normalize(search) !== '' ||
    (mentorEnabled && normalize(mentorSearch) !== '') ||
    statusSet.size > 0 ||
    sortKey !== 'newest';

  const clearAll = () => {
    setSearch('');
    setMentorSearch('');
    setStatusSet(new Set());
    setSortKey('newest');
  };

  // Minimal fallback UI for errors/loading if you use this standalone without wrapper
  if (error && onRetry) {
    return (
      <Card
        className={['rounded-2xl border bg-card', className]
          .filter(Boolean)
          .join(' ')}
      >
        <div className="p-4 sm:p-5">
          <div className="flex items-center justify-between gap-3">
            <div className="text-sm text-destructive">Failed to load.</div>
            <Button
              variant="outline"
              className="rounded-full bg-transparent"
              onClick={onRetry}
            >
              Retry
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card
      className={[
        'rounded-2xl bg-card mt-0 shadow-sm border-none p-0 bg-transparent',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className="p-4 sm:p-5">
        {/* Single Row Toolbar: Title + Search + Filters + Create */}
        <div className="flex flex-wrap items-center gap-2 mb-5">
          {/* Title + Count */}
          <div className="flex items-center gap-1.5 mr-2">
            <h2 className="text-base font-semibold whitespace-nowrap">
              {title}
            </h2>
            <Badge
              variant="secondary"
              className="rounded-full text-xs px-1.5 py-0"
            >
              {filtered.length}
            </Badge>
            {(isLoading || isFetching) && (
              <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" />
            )}
          </div>

          {/* Search Input */}
          <div className="relative flex-1 min-w-[140px] max-w-xs">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search…"
              className="h-8 pr-7 rounded-md text-sm"
            />
            {normalize(search) !== '' && (
              <button
                type="button"
                onClick={() => setSearch('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </div>

          {/* Filters Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="rounded-md h-8 px-2.5 gap-1 bg-transparent"
              >
                <SlidersHorizontal className="h-3.5 w-3.5" />
                {(statusSet.size > 0 ||
                  (mentorEnabled && normalize(mentorSearch) !== '') ||
                  sortKey !== 'newest') && (
                    <Badge
                      variant="secondary"
                      className="rounded-full text-[10px] px-1.5 py-0"
                    >
                      {statusSet.size +
                        (mentorEnabled && normalize(mentorSearch) !== ''
                          ? 1
                          : 0) +
                        (sortKey !== 'newest' ? 1 : 0)}
                    </Badge>
                  )}
              </Button>
            </DropdownMenuTrigger>

            <DropdownMenuContent align="end" className="w-64">
              {mentorEnabled && (
                <>
                  <DropdownMenuLabel className="text-xs font-medium text-muted-foreground uppercase tracking-wide py-1">
                    Mentor
                  </DropdownMenuLabel>
                  <div className="px-2 pb-2">
                    <Input
                      value={mentorSearch}
                      onChange={(e) => setMentorSearch(e.target.value)}
                      placeholder="Search mentor…"
                      className="h-7 text-sm rounded"
                    />
                  </div>
                  <DropdownMenuSeparator />
                </>
              )}

              <DropdownMenuLabel className="text-xs font-medium text-muted-foreground uppercase tracking-wide py-1">
                Status
              </DropdownMenuLabel>
              <div className="max-h-36 overflow-auto">
                {allStatuses.length === 0 ? (
                  <div className="px-3 py-1.5 text-xs text-muted-foreground">
                    No statuses
                  </div>
                ) : (
                  allStatuses.map((st) => (
                    <DropdownMenuCheckboxItem
                      key={st}
                      checked={statusSet.has(st)}
                      onCheckedChange={(checked) => {
                        setStatusSet((prev) => {
                          const next = new Set(prev);
                          if (checked) next.add(st);
                          else next.delete(st);
                          return next;
                        });
                      }}
                      className="text-sm py-1"
                    >
                      {st}
                    </DropdownMenuCheckboxItem>
                  ))
                )}
              </div>

              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-xs font-medium text-muted-foreground uppercase tracking-wide py-1 flex items-center gap-1">
                <ArrowUpDown className="h-3 w-3" />
                Sort
              </DropdownMenuLabel>

              {(
                [
                  { key: 'newest', label: 'Newest' },
                  { key: 'oldest', label: 'Oldest' },
                  { key: 'name_az', label: 'A → Z' },
                  { key: 'name_za', label: 'Z → A' },
                  { key: 'duration_high', label: 'Duration ↓' },
                  { key: 'duration_low', label: 'Duration ↑' },
                ] as const
              ).map((s) => (
                <DropdownMenuCheckboxItem
                  key={s.key}
                  checked={sortKey === s.key}
                  onCheckedChange={() => setSortKey(s.key)}
                  className="text-sm py-1"
                >
                  {s.label}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Clear Button */}
          {hasActive && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="rounded-md h-8 px-2 text-muted-foreground hover:text-foreground"
              onClick={clearAll}
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          )}

          {/* Tabs (if provided) */}
          {underlinedTabs}

          {/* Create Button - pushed to end */}
          {onCreate && (
            <Button
              type="button"
              size="sm"
              onClick={onCreate}
              title={createLabel}
              className="ml-auto"
            >
              <Plus className="h-4 w-4 sm:mr-1" />
              <span className="hidden sm:inline text-sm">{createLabel}</span>
            </Button>
          )}
        </div>

        {/* Active Filters Pills - Inline and compact */}
        {hasActive && (
          <div className="mt-3 flex flex-wrap items-center gap-1.5">
            {normalize(search) !== '' && (
              <Badge
                variant="outline"
                className="rounded-md text-xs px-2 py-0.5 gap-1 font-normal cursor-pointer hover:bg-muted"
                onClick={() => setSearch('')}
              >
                {search}
                <X className="h-3 w-3" />
              </Badge>
            )}
            {mentorEnabled && normalize(mentorSearch) !== '' && (
              <Badge
                variant="outline"
                className="rounded-md text-xs px-2 py-0.5 gap-1 font-normal cursor-pointer hover:bg-muted"
                onClick={() => setMentorSearch('')}
              >
                Mentor: {mentorSearch}
                <X className="h-3 w-3" />
              </Badge>
            )}
            {Array.from(statusSet).map((st) => (
              <Badge
                key={st}
                variant="outline"
                className="rounded-md text-xs px-2 py-0.5 gap-1 font-normal cursor-pointer hover:bg-muted"
                onClick={() => {
                  setStatusSet((prev) => {
                    const next = new Set(prev);
                    next.delete(st);
                    return next;
                  });
                }}
              >
                {st}
                <X className="h-3 w-3" />
              </Badge>
            ))}
            {sortKey !== 'newest' && (
              <Badge
                variant="outline"
                className="rounded-md text-xs px-2 py-0.5 gap-1 font-normal cursor-pointer hover:bg-muted"
                onClick={() => setSortKey('newest')}
              >
                {sortKey.replace('_', ' ')}
                <X className="h-3 w-3" />
              </Badge>
            )}
          </div>
        )}
        <WithLoadingErrorWrapper
          loading={isLoading}
          error={error as ApiErrorResponse}
          isFetching={isFetching}
          onRetry={onRetry}
        >
          {/* List */}
          <div className="mt-4">
            <ShowProjects
              projects={filtered}
              projectType={projectType}
              assignedInternalProjects={!!assignedInternalProjects || !!assignedExternalProjects}
            />
          </div>
        </WithLoadingErrorWrapper>
      </div>
    </Card>
  );
};
